import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShareButton from '../common/ShareButton';
import './UseCaseModal.css';

const UseCaseModal = ({ isOpen, onClose, data }) => {
    // 1. Hooks (Top Level)
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);
    const [estimateHover, setEstimateHover] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setCurrentImageIndex(0);
            setIsZoomed(false);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, data]);

    // 2. Early Return
    if (!isOpen || !data) return null;

    // 3. Helper Variables & Functions
    const images = data?.images && data.images.length > 0 ? data.images : (data?.image ? [data.image] : []);
    const hasMultipleImages = images.length > 1;

    const handleNext = (e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = (e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const toggleZoom = () => {
        setIsZoomed(!isZoomed);
    };

    // 4. Sub-components (Zoom Overlay)
    const ZoomOverlay = () => (
        <div
            style={{
                position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                background: 'rgba(0,0,0,0.9)', zIndex: 3000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'zoom-out'
            }}
            onClick={toggleZoom}
        >
            {images.length > 0 && (
                <img
                    src={images[currentImageIndex]}
                    alt="Zoomed"
                    style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain' }}
                />
            )}
            <button
                onClick={toggleZoom}
                style={{
                    position: 'absolute', top: '20px', right: '20px',
                    background: 'none', border: 'none', color: 'white', fontSize: '2rem', cursor: 'pointer'
                }}
            >
                &times;
            </button>

            {hasMultipleImages && (
                <>
                    <button onClick={handlePrev} style={{
                        position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none',
                        width: '50px', height: '50px', borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>&#10094;</button>
                    <button onClick={handleNext} style={{
                        position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none',
                        width: '50px', height: '50px', borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>&#10095;</button>
                </>
            )}
        </div>
    );

    // 5. Main Render
    return (
        <div className="usecase-modal-overlay" onClick={onClose}>
            <div className="usecase-modal-content" onClick={e => e.stopPropagation()}>
                <div className="usecase-modal-header">
                    <div>
                        <span className="usecase-modal-category">{data.category?.label || data.category}</span>
                        <h2 className="usecase-modal-title">{data.title}</h2>
                    </div>
                    <button className="usecase-modal-close" onClick={onClose}>&times;</button>
                </div>

                <div className="usecase-modal-body">
                    {/* Image Carousel Section */}
                    {images.length > 0 && (
                        <div className="usecase-carousel-container" style={{ position: 'relative', marginBottom: '2rem', borderRadius: '8px', overflow: 'hidden', background: '#f5f5f5' }}>
                            <div
                                style={{ width: '100%', height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in' }}
                                onClick={toggleZoom}
                            >
                                <img
                                    src={images[currentImageIndex]}
                                    alt={`${data.title} ${currentImageIndex + 1}`}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </div>

                            {/* Navigation Arrows */}
                            {hasMultipleImages && (
                                <>
                                    <button onClick={handlePrev} style={{
                                        position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                                        background: 'rgba(0,0,0,0.3)', color: 'white', border: 'none',
                                        width: '40px', height: '40px', borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s'
                                    }}>&#10094;</button>
                                    <button onClick={handleNext} style={{
                                        position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                                        background: 'rgba(0,0,0,0.3)', color: 'white', border: 'none',
                                        width: '40px', height: '40px', borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s'
                                    }}>&#10095;</button>

                                    {/* Dots Indicator */}
                                    <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px' }}>
                                        {images.map((_, idx) => (
                                            <span key={idx} style={{
                                                width: '8px', height: '8px', borderRadius: '50%',
                                                background: idx === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                                                transition: 'background 0.2s'
                                            }} />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isZoomed && <ZoomOverlay />}

                    <div className="usecase-detail-section">
                        <h4 className="usecase-detail-label">도입 배경 (Problem)</h4>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{data.problem || data.description}</p>
                    </div>

                    <div className="usecase-detail-section">
                        <h4 className="usecase-detail-label">해결 방안 (Solution)</h4>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{data.solution || "가까운솔루션 ESL 시스템 도입 및 맞춤형 레이아웃 적용"}</p>
                    </div>

                    <div className="usecase-detail-section">
                        <h4 className="usecase-detail-label">도입 효과 (Effect)</h4>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{data.effect}</p>
                    </div>

                    {data.examples && data.examples.length > 0 && (
                        <div className="usecase-detail-section">
                            <h4 className="usecase-detail-label">📌 주요 활용처</h4>
                            <ul style={{ paddingLeft: '1.2rem', color: '#555' }}>
                                {data.examples.map((ex, i) => <li key={i}>{ex}</li>)}
                            </ul>
                        </div>
                    )}

                    {data.appliedCompanies && data.appliedCompanies.length > 0 && (
                        <div className="usecase-detail-section">
                            <h4 className="usecase-detail-label">🏢 도입 기업</h4>
                            <ul style={{ paddingLeft: '1.2rem', listStyleType: 'disc' }}>
                                {data.appliedCompanies.map((comp, i) => (
                                    <li key={i} style={{ color: '#ff6969', marginBottom: '4px' }}>
                                        <span style={{ color: '#333', fontWeight: '600' }}>{comp}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Social Share Module + Estimate Floater (when category has a related version) */}
                    <ShareButton
                        url={`${window.location.origin}/use-cases/${data.id}`}
                        title={`[가까운솔루션 구축사례] ${data.title}`}
                        description={data.description}
                        imageUrl={images && images.length > 0 ? images[0] : null}
                        rightExtra={data.category?.relatedVersionId ? (
                            <div
                                style={{ position: 'relative' }}
                                onMouseEnter={() => setEstimateHover(true)}
                                onMouseLeave={() => setEstimateHover(false)}
                            >
                                <button
                                    type="button"
                                    onClick={() => navigate('/estimate', { state: { versionId: data.category.relatedVersionId } })}
                                    aria-label="이 솔루션의 3분 셀프견적 시작하기"
                                    style={{
                                        width: '56px', height: '56px',
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        background: 'var(--primary-color)', color: 'white',
                                        border: 'none', borderRadius: '50%',
                                        cursor: 'pointer',
                                        boxShadow: estimateHover
                                            ? '0 8px 20px rgba(255,51,102,0.45)'
                                            : '0 4px 14px rgba(255,51,102,0.35)',
                                        transform: estimateHover ? 'scale(1.06)' : 'scale(1)',
                                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                    }}
                                >
                                    <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>🚀</span>
                                    <span style={{
                                        fontSize: '8px', fontWeight: 800, letterSpacing: '-0.02em',
                                        marginTop: '2px', lineHeight: 1, whiteSpace: 'nowrap'
                                    }}>
                                        3분견적
                                    </span>
                                </button>

                                {/* Hover tooltip (desktop). On touch devices it'll briefly flash on tap. */}
                                <div
                                    role="tooltip"
                                    style={{
                                        position: 'absolute',
                                        bottom: 'calc(100% + 8px)',
                                        right: 0,
                                        background: '#1F2937', color: 'white',
                                        padding: '8px 12px', borderRadius: '8px',
                                        fontSize: '0.78rem', fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                                        opacity: estimateHover ? 1 : 0,
                                        transform: estimateHover ? 'translateY(0)' : 'translateY(4px)',
                                        transition: 'opacity 0.15s ease, transform 0.15s ease',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    지금 이 솔루션 → 3분 셀프견적
                                    <span style={{
                                        position: 'absolute', top: '100%', right: '20px',
                                        width: 0, height: 0,
                                        borderLeft: '6px solid transparent',
                                        borderRight: '6px solid transparent',
                                        borderTop: '6px solid #1F2937'
                                    }} />
                                </div>
                            </div>
                        ) : null}
                    />
                </div>
            </div>
        </div>
    );
};

export default UseCaseModal;
